import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {IEmojiReactionData} from '../../model/emojiReactionsModel';
import {reactionsStore, updateReactionsState} from '../../context/reactionsProvider';
import {getNativeEmojiById} from '../../util/EmojiSearch';
import {importMediaInterface} from '../../hooks/dynamicImport';
import {events} from '@techsmith/techsmith-smartplayer';

const ReactionsPlaybackPlugin = styled.div<{controlsHidden: boolean}>`
   position: absolute;
   display: ${props => props.controlsHidden ? 'block' : 'none'};
   bottom: 0;
   width: 100%;
   height: 2rem;
`;

const timeBufferToTriggerReactionSeconds = 0.5;
const reactionDataAttributeName = 'data-reaction-id';
const numberCssAnimations = 5;

export const ReactionPlaybackPluginBase: React.FC<IReactionsPlaybackPluginProps & IStateMappedProps> = ({mediaDuration, reactions}) => {
   const reactionContainerRef = useRef<HTMLDivElement>(null);
   const mediaDurationRef = useRef(mediaDuration);
   const controlsHiddenRef = useRef<boolean>(false);
   const reactionsRef = useRef<IEmojiReactionData[]>(reactions);
   const [controlsHidden, setControlsHidden] = useState(false);
   const displayedReactions = new Map<string, HTMLDivElement>();

   const clearAllAnimatedReactions = () => {
      displayedReactions.forEach(reactionElement => {
         reactionElement.removeEventListener('animationend', onAnimationEnd);
         reactionElement.remove();
      });
      displayedReactions.clear();
   };

   const onAnimationEnd = (e: AnimationEvent) => {
      const reactionDisplayElement = e.target as HTMLDivElement;
      const reactionId = reactionDisplayElement.getAttribute(reactionDataAttributeName);
      reactionDisplayElement.removeEventListener('animationend', onAnimationEnd);
      reactionDisplayElement.remove();
      displayedReactions.delete(reactionId);
   };

   const createAnimatedReaction = (reaction: IEmojiReactionData) => {
      const newReaction = document.createElement('div');
      const randomAnimationClass = `reactionAnimation${Math.ceil(Math.random() * numberCssAnimations)}`;
      newReaction.setAttribute(reactionDataAttributeName, reaction.ReactionExternalId);
      newReaction.innerHTML = getNativeEmojiById(reaction.EmojiId);
      newReaction.classList.add('reactionPlayback', randomAnimationClass);
      newReaction.style.left = `${Math.max(Math.min(reaction.Time / mediaDurationRef.current * 100, 100), 0)}%`;
      newReaction.addEventListener('animationend', onAnimationEnd);

      displayedReactions.set(reaction.ReactionExternalId, newReaction);

      reactionContainerRef.current.appendChild(newReaction);
   };

   const findReactionsToDisplayByTime = (videoTime: number): IEmojiReactionData[] => reactionsRef.current.filter(reaction => !displayedReactions.get(reaction.ReactionExternalId) && reaction.Time >= videoTime && reaction.Time < videoTime + timeBufferToTriggerReactionSeconds || reaction.InitialDisplay);
   const findNewReactionsToDisplay = (): IEmojiReactionData[] => reactionsRef.current.filter(reaction => !displayedReactions.get(reaction.ReactionExternalId) && reaction.InitialDisplay);

   const displayReactionsByTime = async () => {
      const mediaInterface = await importMediaInterface();
      if (!controlsHiddenRef.current || mediaInterface.playbackRate < 1) {
         return;
      }
      const reactionsToDisplay = findReactionsToDisplayByTime(mediaInterface.currentTime);
      reactionsToDisplay.forEach(reaction => {
         createAnimatedReaction(reaction);
      });
   };

   const displayNewReactions = () => {
      let hasInitialDisplayedReaction = false;
      const reactionsToDisplay = findNewReactionsToDisplay();
      reactionsToDisplay.forEach(reaction => {
         if (controlsHiddenRef.current) {
            createAnimatedReaction(reaction);
         }

         if (reaction.InitialDisplay) {
            hasInitialDisplayedReaction = true;
         }
      });

      if (hasInitialDisplayedReaction) {
         reactions.forEach(reaction => {
            reaction.InitialDisplay = false;
         });
         updateReactionsState({reactions: reactions});
      }
   };

   const onSmartPlayerControlsHidden = () => {
      controlsHiddenRef.current = true;
      setControlsHidden(true);
   };

   const onSmartPlayerControlsVisible = () => {
      controlsHiddenRef.current = false;
      setControlsHidden(false);
      clearAllAnimatedReactions();
   };

   useEffect(() => {
      void importMediaInterface().then(mediaInterface => {
         mediaInterface.addEventListener(events.ExternalVideo.VideoTimeUpdate, displayReactionsByTime);
         mediaInterface.addEventListener(events.External.PlayerControlsHidden, onSmartPlayerControlsHidden);
         mediaInterface.addEventListener(events.External.PlayerControlsVisible, onSmartPlayerControlsVisible);
      });
      return () => {
         void importMediaInterface().then(mediaInterface => {
            mediaInterface.removeEventListener(events.ExternalVideo.VideoTimeUpdate, displayReactionsByTime);
            mediaInterface.removeEventListener(events.External.PlayerControlsHidden, onSmartPlayerControlsHidden);
            mediaInterface.removeEventListener(events.External.PlayerControlsVisible, onSmartPlayerControlsVisible);
         });
      };
   }, []);

   useEffect(() => {
      mediaDurationRef.current = mediaDuration;
   }, [mediaDuration]);

   useEffect(() => {
      reactionsRef.current = reactions;
      displayNewReactions();
   }, [reactions]);

   return (
      <ReactionsPlaybackPlugin
         ref={reactionContainerRef}
         controlsHidden={controlsHidden}
      />
   );
};

export interface IReactionsPlaybackPluginProps {
   mediaDuration: number;
}

export interface IStateMappedProps {
   reactions: IEmojiReactionData[];
}

const mapStatesToProps = (
   {mediaDuration}: IReactionsPlaybackPluginProps,
   {reactions}: Partial<IStateMappedProps>
): IReactionsPlaybackPluginProps & IStateMappedProps => ({mediaDuration, reactions});

export default withMemoizedContexts(mapStatesToProps, reactionsStore)(ReactionPlaybackPluginBase);
